/**
 * Shared types for the import review UI (GDN-2178 / ADR-0076).
 *
 * The verdict + decision vocabulary the ImportReviewBrowser and its host
 * (StagedResourcesSection) speak. Kept in a small neutral module so the live
 * components don't depend on each other's files for types.
 */

/** Classify verdict for a staged record. */
export type ImportVerdict =
  | 'new'
  | 'unchanged'
  | 'update'
  | 'conflict'
  | 'ambiguous'
  | 'unprocessable';

/** verdict -> count; the backend zero-fills every lane. */
export type VerdictCounts = Record<ImportVerdict, number>;

/**
 * One existing-patient candidate for an ambiguous match (PHI; UI-only).
 *
 * Identity fields are carried structured (not a pre-joined label) so the UI can
 * render a scannable name · DOB · MRN row and, crucially, never fall back to a
 * raw patient UUID when the name is missing (GDN-2344). `patientId` is a link
 * target, never a display string.
 */
export interface ReviewMatchCandidate {
  patientId: string;
  /** Existing patient's display name, if known. Absent → show a graceful fallback, never the id. */
  name?: string;
  /** Existing patient's date of birth, if known. */
  dob?: string;
  /** The candidate's identifier/MRN — decision evidence (PHI; UI-only). */
  identifier?: string;
  /**
   * How the candidate matched: 'identifier' (MRN), 'identifier_only'
   * (MRN collides but demographics disagree), or 'demographic'.
   */
  reason: string;
  /** Match confidence 0–100. Retained for ordering; not shown to the operator (GDN-2344). */
  confidence: number;
}

/**
 * Human label for a candidate — never the raw patient id (GDN-2344). A missing
 * name yields a neutral "Existing patient"; the DOB/MRN evidence line and the
 * patient-page link carry the actual identifying detail.
 */
export function candidateDisplayName(c: ReviewMatchCandidate): string {
  return c.name?.trim() || 'Existing patient';
}

/** Operator resolution for an ambiguous patient match (GDN-2212). */
export type ReviewMatchResolution =
  | { action: 'link'; targetPatientId: string }
  | { action: 'create' };

/** One field's existing-vs-incoming values (PHI; UI-only, never logged). */
export interface ReviewFieldDiff {
  field: string;
  /** Current value on the live record. */
  existing: string;
  /** Value coming from the import. */
  incoming: string;
}

/**
 * Operator decision for a conflicting record.
 *
 * NOTE (GDN-2300): `escalate` ("Send to admin") was removed. Org-level review is
 * no longer a staged commit-time conflict decision — it's now an orthogonal,
 * persisted, toggleable org-review flag (see `setOrgReview` in store/api.ts and
 * the `orgReview` prop on ImportReviewBrowser), mirroring the GDN-2297 selection
 * dimension. The remaining values are the field-merge / whole-record decisions.
 */
export type ReviewDecision =
  | 'keep_existing' // competing fields: existing value wins
  | 'take_import' // competing fields: incoming value wins
  | 'import_as_new' // the match is wrong — import as a different record
  | 'skip'; // drop this record from the import
